import { Form, FormikProps } from 'formik';
import { FormInputWithLabel } from '../../../_core/form/input/form-labelled-input.component';
import { PropsWithChildren, useMemo } from 'react';
import { ISendCustomRequestMessageParams } from '../../request/request.types';
import { IRequest, REQUEST_NOTIFY_OPTION, requestNotifyOptionMultiselectLabels } from '../../../../lib/types';
import { IMultiSelectOption } from '../../../_core/input/multiselect-input.component';
import _ from 'lodash';
import { getName } from '../../../../utils/name.utils';

export interface ISendCustomRequestMessageFormParams
  extends Omit<ISendCustomRequestMessageParams, '_id' | 'notify' | 'to' | 'replyTo'> {
  notify?: IMultiSelectOption[];
  to?: IMultiSelectOption[];
  groupsToggle?: boolean;
  replyTo: IMultiSelectOption[];
}

interface ISendRequestMessageFormFieldsParams
  extends FormikProps<ISendCustomRequestMessageFormParams>,
    PropsWithChildren {
  clientMode?: boolean;
  forceSendAll?: boolean;
  replyToOptions: IMultiSelectOption[];
  request: IRequest;
  userId: string;
}

export const SendRequestMessageFormFields = ({
  children,
  clientMode,
  forceSendAll,
  replyToOptions,
  request,
  userId,
  values
}: ISendRequestMessageFormFieldsParams) => {
  const toOptions = useMemo(() => {
    const options: IMultiSelectOption[] = [];

    if (!clientMode) {
      if (request.assignedTo && request.assignedTo.user._id !== userId)
        options.push({
          label: getName(request.assignedTo),
          secondaryLabel: 'Assignee - ' + request.assignedTo.user.email,
          value: request.assignedTo?._id,
          group: 'Contacts'
        });

      options.push(
        ...[...(request.clientEditorIds ?? []), ...(request.clientViewerIds ?? [])]
          .filter(({ user }) => user._id !== userId)
          .map((c) => ({ label: getName(c), secondaryLabel: c.user.email, value: c._id, group: 'Contacts' }))
      );
    }

    options.push({ label: getName(request.createdBy), value: request.createdBy._id, group: 'Team Members' });

    options.push(
      ...[...(request.staffEditorIds ?? []), ...(request.staffViewerIds ?? [])]
        .filter(({ user }) => user._id !== userId)
        .map((m) => ({ label: getName(m), secondaryLabel: m.user.email, value: m._id, group: 'Team Members' }))
    );

    return _.uniqBy(options, ({ value }) => value).filter(({ value }) => value !== userId);
  }, [
    clientMode,
    request.assignedTo,
    request.clientEditorIds,
    request.clientViewerIds,
    request.createdBy,
    request.staffEditorIds,
    request.staffViewerIds,
    userId
  ]);

  return (
    <Form action="#" method="POST" className="sm:max-w-xl mx-auto space-y-4 mt-10">
      {!forceSendAll && (
        <>
          {!clientMode && <FormInputWithLabel name="groupsToggle" label="Send to groups" type="checkbox" />}
          {values.groupsToggle ? (
            <FormInputWithLabel
              name="notify"
              label="To:"
              placeholder="Notify groups..."
              type="multiselect"
              multiSelectProps={{
                options: [
                  {
                    label: requestNotifyOptionMultiselectLabels[REQUEST_NOTIFY_OPTION.CLIENTS],
                    value: REQUEST_NOTIFY_OPTION.CLIENTS
                  },
                  {
                    label: requestNotifyOptionMultiselectLabels[REQUEST_NOTIFY_OPTION.STAFF],
                    value: REQUEST_NOTIFY_OPTION.STAFF
                  }
                ],
                selectAll: true
              }}
            />
          ) : (
            <FormInputWithLabel
              name="to"
              label="To:"
              type="multiselect"
              placeholder="To"
              multiSelectProps={{
                options: toOptions,
                groupOrder: clientMode ? undefined : ['Contacts', 'Team Members']
              }}
            />
          )}
        </>
      )}
      <FormInputWithLabel
        name="replyTo"
        label="Reply To:"
        type="multiselect"
        placeholder="Select email"
        multiSelectProps={{ singleSelect: true, options: replyToOptions, nullable: true }}
      />
      <FormInputWithLabel
        name="subject"
        label="Subject:"
        type="text"
        placeholder="You have received a new message on one of your Requests"
      />
      <FormInputWithLabel name="message" label="Message:" placeholder="Enter your message here..." type="textarea" />
      {children}
    </Form>
  );
};
